/**
 * Optimize an image on its way into a snapshot.
 *
 * A snapshot carries its assets inline, so an unoptimized PNG is dead weight in
 * every playable built from it. The media library has optimized uploads for
 * years; nothing on the snapshot path did, because these bytes never go through the
 * uploader that does it.
 *
 * WHERE this sits matters: it runs on the source about to be embedded, not on
 * the file the user picked and not on the copy kept in the library. Those stay
 * exactly as they were — only the snapshot's own copy is optimized.
 *
 * Honours the legacy `settings.autoCompress` storage key, the same switch the rest of the dashboard
 * reads, so one preference governs the lot.
 */

import { optimizeImageSource } from "./snapshotEditorApi";

/** Which half of a pair type holds the image; the metadata half is left alone. */
const PAIR_IMAGE_KEY = "image";

export function isAutoOptimizeEnabled() {
  return localStorage.getItem("settings.autoCompress") !== "false";
}

/** Only raster formats Sharp can optimize; SVG/GIF/WebP pass straight through. */
function isOptimizableDataUrl(value) {
  return /^data:image\/(png|jpe?g);base64,/i.test(String(value || ""));
}

async function optimizeOne(baseID, source, fileName, onSaved) {
  if (!isOptimizableDataUrl(source)) return source;
  try {
    const result = await optimizeImageSource(baseID, { source, fileName });
    if (!result.ok) return source;
    if (result.data?.savedBytes > 0) onSaved?.(result.data);
    return result.data?.source || source;
  } catch (_err) {
    // A snapshot with a big image beats no snapshot at all.
    return source;
  }
}

/**
 * Optimize whatever part of `source` is an image.
 *
 * @param {object} opts
 * @param {string} opts.baseID
 * @param {string} opts.assetType
 * @param {*}      opts.source    data URL, or `{ image, xml|json }` for a pair
 * @param {string} [opts.fileName] used to guess the format when the mime is vague
 * @param {function} [opts.onSaved] called with the optimization stats when it helped
 * @returns {Promise<*>} the same shape as `source`
 */
export async function optimizeSourceForSnapshot({ baseID, assetType, source, fileName, onSaved }) {
  if (!baseID || !source || !isAutoOptimizeEnabled()) return source;

  // Plain images only, and deliberately so.
  //
  // The optimizer encodes PNG as a 256-colour palette, which is lossy — it was
  // measured at up to 113 per-channel drift during the export font work, which
  // is why that path refuses palette output. A bitmap-font atlas is anti-aliased
  // glyph edges and an atlas is packed sprite art; quantising either trades
  // visible quality for bytes on exactly the assets that show it first. They are
  // also the minority of the weight: the library runs ~1000 plain images to ~400
  // bitmap fonts.
  //
  // Audio, video and fonts are not ours to re-encode at all.
  if (assetType && assetType !== "image") return source;

  if (typeof source === "string") {
    return optimizeOne(baseID, source, fileName, onSaved);
  }

  // A pair reaching here would be an image-typed one; optimize its image half
  // only, since rewriting the metadata half would break the coordinates it holds.
  if (source[PAIR_IMAGE_KEY]) {
    const image = await optimizeOne(baseID, source[PAIR_IMAGE_KEY], fileName, onSaved);
    return { ...source, [PAIR_IMAGE_KEY]: image };
  }

  return source;
}
